import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Circle, GoogleMap, Marker, Polyline, useJsApiLoader } from "@react-google-maps/api";
import { useTheme } from "../../../context/ThemeContext";
import { useUserStore } from "../../../store/userStore";
import { useSupportChatStore } from "../../../store/supportChatStore";
import type { Conversation, ChatParticipant, TripVehicleInfo } from "../../../store/supportChatStore";
import {
  useRoutesStore,
  type RouteCoordinate,
  type DenseRouteCoordinate,
  type RouteAxisSource,
  ROAD_FOLLOWING_AXIS_SOURCES,
} from "../../../store/admin/routesStore";
import { useTripsStore, type Trip, type TripAssignment } from "../../../store/admin/tripsStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faUser,
  faUsers,
  faTruck,
  faCircle,
  faEnvelope,
  faCalendarAlt,
  faRoute,
  faIdBadge,
  faUserShield,
  faSatelliteDish,
  faTrailer,
  faUserTie,
  faPhone,
  faClock,
  faRoad,
  faSignal,
  faInfoCircle,
  faPlay,
  faFlagCheckered,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

interface ConversationDetailsPanelProps {
  conversation: Conversation;
  onClose: () => void;
}

interface LatLng {
  lat: number;
  lng: number;
}

const MAP_LIBRARIES: ("places")[] = ["places"];

export default function ConversationDetailsPanel({
  conversation,
  onClose,
}: ConversationDetailsPanelProps) {
  const { t } = useTranslation("support");
  const { isDarkMode } = useTheme();
  const currentUser = useUserStore((state) => state.user);
  const onlineUsers = useSupportChatStore((s) => s.onlineUsers);
  const fetchRoute = useRoutesStore((s) => s.fetchRoute);
  const fetchTrip = useTripsStore((s) => s.fetchTrip);

  const others = useMemo(
    () => conversation.participants.filter((p) => p.uid !== currentUser?.id),
    [conversation.participants, currentUser?.id],
  );
  const isOneToOne = conversation.type === "one-to-one";
  const isTrip = !!conversation.trip_id;
  const contact = isOneToOne ? others[0] : undefined;

  const [routeCoords, setRouteCoords] = useState<RouteCoordinate[] | null>(null);
  const [denseRouteCoords, setDenseRouteCoords] = useState<DenseRouteCoordinate[] | null>(null);
  const [routeSource, setRouteSource] = useState<RouteAxisSource | null>(null);
  const [firstPointRadius, setFirstPointRadius] = useState<number | null>(null);
  const [lastPointRadius, setLastPointRadius] = useState<number | null>(null);
  const [routeDistanceKm, setRouteDistanceKm] = useState<number | null>(null);
  const [routeDurationMin, setRouteDurationMin] = useState<number | null>(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [trip, setTrip] = useState<Trip | null>(null);
  const [tripLoading, setTripLoading] = useState(false);

  useEffect(() => {
    if (!isTrip) return;
    let cancelled = false;

    const loadRoute = async (routeId: string) => {
      const result = await fetchRoute(routeId);
      if (cancelled || !result.success || !result.data) return;
      setRouteCoords(result.data.principal_route || []);
      setDenseRouteCoords(result.data.principal_route_dense || null);
      setRouteSource(result.data.principal_route_source || null);
      setFirstPointRadius(result.data.first_point_radius ?? null);
      setLastPointRadius(result.data.last_point_radius ?? null);
      setRouteDistanceKm(result.data.principal_route_distance ?? null);
      setRouteDurationMin(result.data.principal_route_duration ?? null);
    };

    const run = async () => {
      // Trip + route fetch in parallel when both ids are known up-front.
      const tripPromise = conversation.trip_id
        ? (setTripLoading(true), fetchTrip(conversation.trip_id))
        : Promise.resolve(null);

      const initialRouteId = conversation.route_id;
      if (initialRouteId) setRouteLoading(true);
      const initialRoutePromise = initialRouteId
        ? loadRoute(initialRouteId)
        : Promise.resolve();

      const tripResult = await tripPromise;
      if (!cancelled) {
        setTrip(tripResult?.success ? tripResult.data ?? null : null);
        setTripLoading(false);
      }

      await initialRoutePromise;
      if (!cancelled && initialRouteId) setRouteLoading(false);

      // Fallback: conversation didn't store route_id but the trip does.
      const fallbackRouteId = tripResult?.data?.route_id;
      if (!cancelled && !initialRouteId && fallbackRouteId) {
        setRouteLoading(true);
        await loadRoute(fallbackRouteId);
        if (!cancelled) setRouteLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [isTrip, conversation.trip_id, conversation.route_id, fetchRoute, fetchTrip]);

  // Choose the most relevant assignment to surface (next upcoming, otherwise
  // the currently active one, otherwise the latest).
  const featuredAssignment = useMemo<TripAssignment | null>(() => {
    const list = trip?.assignments;
    if (!list || list.length === 0) return null;
    const now = Date.now();
    const parsed = list
      .map((a) => ({
        a,
        start: a.start_time ? new Date(a.start_time).getTime() : 0,
        end: a.end_time ? new Date(a.end_time).getTime() : 0,
      }))
      .sort((x, y) => x.start - y.start);

    const active = parsed.find(({ start, end }) => start <= now && now <= end);
    if (active) return active.a;
    const upcoming = parsed.find(({ start }) => start > now);
    if (upcoming) return upcoming.a;
    return parsed[parsed.length - 1].a;
  }, [trip?.assignments]);

  function formatDate(ts: number): string {
    return new Date(ts).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  function formatDateTime(value?: string): string {
    if (!value) return "—";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // Avatar colour
  const avatarBg = isTrip
    ? "bg-amber-500"
    : conversation.type === "group"
      ? "bg-violet-500"
      : "bg-primary-500";

  const avatarIcon = isTrip ? faTruck : isOneToOne ? faUser : faUsers;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
      />

      {/* Panel – slides in from right */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-sm shadow-2xl flex flex-col animate-slide-in-right ${
          isDarkMode ? "bg-dark-surface" : "bg-white"
        }`}
      >
        {/* Header band */}
        <div
          className={`relative flex flex-col items-center pt-6 pb-8 ${
            isTrip
              ? "bg-gradient-to-b from-amber-500/20 to-transparent"
              : conversation.type === "group"
                ? "bg-gradient-to-b from-violet-500/20 to-transparent"
                : "bg-gradient-to-b from-primary-500/20 to-transparent"
          }`}
        >
          <button
            onClick={onClose}
            className={`absolute top-3 right-3 p-2 rounded-full transition-colors ${
              isDarkMode
                ? "hover:bg-dark-hover text-gray-400"
                : "hover:bg-gray-100 text-gray-500"
            }`}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          {/* Large avatar */}
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center ${avatarBg} mb-3`}
          >
            <FontAwesomeIcon icon={avatarIcon} className="text-white text-2xl" />
          </div>

          {/* Name */}
          <h2
            className={`text-lg font-bold text-center px-6 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {isOneToOne
              ? contact?.display_name ?? t("chat.you")
              : conversation.name ?? t("details.group")}
          </h2>

          {/* Sub-label */}
          {isTrip && (
            <span
              className={`mt-1 text-xs font-medium px-2 py-0.5 rounded ${
                isDarkMode
                  ? "bg-amber-500/20 text-amber-400"
                  : "bg-amber-100 text-amber-700"
              }`}
            >
              {conversation.trip_id}
            </span>
          )}

          {isOneToOne && contact && (
            <div className="flex items-center gap-1.5 mt-1">
              <FontAwesomeIcon
                icon={faCircle}
                className={`text-[8px] ${
                  onlineUsers[contact.uid] ? "text-green-500" : "text-gray-400"
                }`}
              />
              <span
                className={`text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {onlineUsers[contact.uid] ? t("chat.online") : t("chat.offline")}
              </span>
            </div>
          )}

          {!isOneToOne && (
            <p
              className={`text-xs mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {conversation.participants.length} {t("details.members")}
            </p>
          )}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-6">
          {/* Trip route map (real Google Map) */}
          {isTrip && (
            <Section title={t("details.routeMap")} isDarkMode={isDarkMode}>
              <TripRouteMap
                coords={routeCoords}
                denseCoords={denseRouteCoords}
                source={routeSource}
                firstPointRadius={firstPointRadius}
                lastPointRadius={lastPointRadius}
                loading={routeLoading}
                isDarkMode={isDarkMode}
              />
              {(routeDistanceKm != null || routeDurationMin != null) && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {routeDistanceKm != null && (
                    <StatTile
                      icon={faRoad}
                      label={t("details.distance")}
                      value={`${routeDistanceKm.toFixed(1)} km`}
                      isDarkMode={isDarkMode}
                    />
                  )}
                  {routeDurationMin != null && (
                    <StatTile
                      icon={faClock}
                      label={t("details.duration")}
                      value={formatDuration(routeDurationMin)}
                      isDarkMode={isDarkMode}
                    />
                  )}
                </div>
              )}
            </Section>
          )}

          {/* Trip schedule (from selected assignment) */}
          {isTrip && (featuredAssignment || tripLoading) && (
            <Section title={t("details.schedule")} isDarkMode={isDarkMode}>
              {tripLoading && !featuredAssignment ? (
                <div className="flex items-center gap-2 text-sm">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary-500" />
                  <span className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                    {t("details.loading")}
                  </span>
                </div>
              ) : featuredAssignment ? (
                <>
                  <InfoRow
                    icon={faPlay}
                    label={t("details.start")}
                    value={formatDateTime(featuredAssignment.start_time)}
                    isDarkMode={isDarkMode}
                  />
                  <InfoRow
                    icon={faFlagCheckered}
                    label={t("details.eta")}
                    value={formatDateTime(featuredAssignment.end_time)}
                    isDarkMode={isDarkMode}
                  />
                  {featuredAssignment.device?.name && (
                    <InfoRow
                      icon={faSatelliteDish}
                      label={t("details.device")}
                      value={
                        featuredAssignment.device.name ||
                        featuredAssignment.device.identifier ||
                        featuredAssignment.device_id
                      }
                      isDarkMode={isDarkMode}
                    />
                  )}
                </>
              ) : null}
            </Section>
          )}

          {/* Info section */}
          <Section title={t("details.info")} isDarkMode={isDarkMode}>
            {/* Company (1:1) */}
            {isOneToOne && contact?.company_name && (
              <InfoRow
                icon={faUsers}
                label={t("details.company")}
                value={contact.company_name}
                isDarkMode={isDarkMode}
              />
            )}

            {/* Email (1:1) */}
            {isOneToOne && contact?.email && (
              <InfoRow
                icon={faEnvelope}
                label={t("details.email")}
                value={contact.email}
                isDarkMode={isDarkMode}
              />
            )}

            {/* Trip name */}
            {isTrip && conversation.trip_name && (
              <InfoRow
                icon={faTruck}
                label={t("details.tripName")}
                value={conversation.trip_name}
                isDarkMode={isDarkMode}
              />
            )}

            {/* Trip description */}
            {isTrip && conversation.trip_description && (
              <div className="flex items-start gap-3">
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  className={`mt-0.5 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                />
                <div className="min-w-0">
                  <p className={`text-[11px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                    {t("details.description")}
                  </p>
                  <p className={`text-sm leading-relaxed ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
                    {conversation.trip_description}
                  </p>
                </div>
              </div>
            )}

            {/* Route name */}
            {isTrip && conversation.route_name && (
              <InfoRow
                icon={faRoute}
                label={t("details.route")}
                value={conversation.route_name}
                isDarkMode={isDarkMode}
              />
            )}

            {/* Trip ID */}
            {isTrip && conversation.trip_id && (
              <InfoRow
                icon={faIdBadge}
                label={t("details.tripId")}
                value={conversation.trip_id}
                isDarkMode={isDarkMode}
              />
            )}

            {/* Created */}
            <InfoRow
              icon={faCalendarAlt}
              label={t("details.created")}
              value={formatDate(conversation.created_at)}
              isDarkMode={isDarkMode}
            />

            {/* Type */}
            <InfoRow
              icon={isOneToOne ? faUser : faUsers}
              label={t("details.type")}
              value={
                isTrip
                  ? t("details.tripConversation")
                  : isOneToOne
                    ? t("details.directMessage")
                    : t("details.groupConversation")
              }
              isDarkMode={isDarkMode}
            />
          </Section>

          {/* Vehicle / Fleet info (trip conversations) */}
          {isTrip && conversation.vehicle_info && (
            <VehicleSection
              info={conversation.vehicle_info}
              isDarkMode={isDarkMode}
              t={t}
            />
          )}

          {/* Participants section (groups & trips) */}
          {!isOneToOne && (
            <Section
              title={`${t("details.participants")} (${conversation.participants.length})`}
              isDarkMode={isDarkMode}
            >
              <div className="space-y-1">
                {conversation.participants.map((p) => {
                  const isMe = p.uid === currentUser?.id;
                  const isOnline = onlineUsers[p.uid] ?? false;
                  return (
                    <ParticipantRow
                      key={p.uid}
                      participant={p}
                      isMe={isMe}
                      isOnline={isOnline}
                      isDarkMode={isDarkMode}
                      t={t}
                    />
                  );
                })}
              </div>
            </Section>
          )}
        </div>
      </div>

      {/* Slide-in keyframe (injected once) */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.25s ease-out;
        }
      `}</style>
    </>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDuration(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ---------------------------------------------------------------------------
// Small sub-components
// ---------------------------------------------------------------------------

function Section({
  title,
  isDarkMode,
  children,
}: {
  title: string;
  isDarkMode: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h4
        className={`text-[11px] font-semibold uppercase tracking-wider mb-3 ${
          isDarkMode ? "text-gray-500" : "text-gray-400"
        }`}
      >
        {title}
      </h4>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
  isDarkMode,
}: {
  icon: typeof faUser;
  label: string;
  value: string;
  isDarkMode: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <FontAwesomeIcon
        icon={icon}
        className={`mt-0.5 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
      />
      <div className="min-w-0">
        <p className={`text-[11px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
          {label}
        </p>
        <p
          className={`text-sm truncate ${
            isDarkMode ? "text-gray-200" : "text-gray-800"
          }`}
        >
          {value}
        </p>
      </div>
    </div>
  );
}

function StatTile({
  icon,
  label,
  value,
  isDarkMode,
}: {
  icon: typeof faUser;
  label: string;
  value: string;
  isDarkMode: boolean;
}) {
  return (
    <div className={`p-2 rounded-md border ${
      isDarkMode ? "bg-dark-hover border-dark-border" : "bg-gray-50 border-gray-200"
    }`}>
      <div className="flex items-center gap-1.5 mb-0.5">
        <FontAwesomeIcon icon={icon} className={`text-[10px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
        <span className={`text-[10px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
          {label}
        </span>
      </div>
      <p className={`text-xs font-semibold ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trip Route Map (real Google Map with polyline)
// ---------------------------------------------------------------------------

function TripRouteMap({
  coords,
  denseCoords,
  source,
  firstPointRadius,
  lastPointRadius,
  loading,
  isDarkMode,
}: {
  coords: RouteCoordinate[] | null;
  denseCoords: DenseRouteCoordinate[] | null;
  source: RouteAxisSource | null;
  firstPointRadius: number | null;
  lastPointRadius: number | null;
  loading: boolean;
  isDarkMode: boolean;
}) {
  const { t } = useTranslation("support");
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || "",
    libraries: MAP_LIBRARIES,
  });

  // Waypoints crudos del usuario (claves en español). Se usan para colocar los
  // marcadores y los círculos de radio en origen/destino.
  const waypointPath = useMemo<LatLng[]>(() => {
    if (!coords) return [];
    return coords
      .filter((c) => c && typeof c.latitud === "number" && typeof c.longitud === "number")
      .map((c) => ({ lat: c.latitud, lng: c.longitud }));
  }, [coords]);

  // Eje densificado que sigue carreteras (claves en inglés). Si el doc lo trae
  // con un `source` de ruteador real ('google' o 'mapbox'), lo dibujamos tal
  // cual. Cuando el doc es legacy (no tiene dense) o el backend cayó al eje
  // recto (dense === waypoints), bypasseamos esto y abajo pedimos Google
  // DirectionsService para empatar el look de `RouteForm`.
  const densePath = useMemo<LatLng[]>(() => {
    if (!denseCoords) return [];
    return denseCoords
      .filter((c) => c && typeof c.latitude === "number" && typeof c.longitude === "number")
      .map((c) => ({ lat: c.latitude, lng: c.longitude }));
  }, [denseCoords]);

  const hasRoadFollowingDense =
    !!source && ROAD_FOLLOWING_AXIS_SOURCES.includes(source) && densePath.length >= 2;

  // Cuando no hay eje que siga carreteras, replicamos el comportamiento de
  // `MapPicker`/`RouteForm`: pedimos a Google DirectionsService el trazado que
  // sigue las carreteras y lo usamos para el polyline. Igual de "mejor ruta"
  // que ve el usuario en la sección de rutas, sin depender de que el backend
  // tenga `principal_route_dense` para la ruta del viaje.
  const [computedPath, setComputedPath] = useState<LatLng[]>([]);
  const [computing, setComputing] = useState(false);

  useEffect(() => {
    if (hasRoadFollowingDense) {
      setComputedPath([]);
      setComputing(false);
      return;
    }
    if (!isLoaded || waypointPath.length < 2) {
      setComputedPath([]);
      setComputing(false);
      return;
    }

    const origin = waypointPath[0];
    const destination = waypointPath[waypointPath.length - 1];
    // Google Directions admite hasta 25 waypoints intermedios; los viajes en
    // esta UI no se acercan a ese límite, pero por seguridad recortamos.
    const intermediates = waypointPath.slice(1, -1).slice(0, 23).map((p) => ({
      location: p,
      stopover: false,
    }));

    const service = new google.maps.DirectionsService();
    let cancelled = false;
    setComputing(true);
    service.route(
      {
        origin,
        destination,
        waypoints: intermediates,
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false,
      },
      (result, status) => {
        if (cancelled) return;
        setComputing(false);
        if (status === google.maps.DirectionsStatus.OK && result?.routes?.[0]) {
          const overview = result.routes[0].overview_path || [];
          setComputedPath(overview.map((p) => ({ lat: p.lat(), lng: p.lng() })));
        } else {
          // Si Google no respondió OK dejamos `computedPath` vacío y arriba
          // caeremos al polyline recto entre waypoints — preferible a no
          // dibujar nada.
          setComputedPath([]);
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [isLoaded, hasRoadFollowingDense, waypointPath]);

  // Orden de preferencia: Mapbox dense → Google directions cliente → polyline
  // recto entre waypoints (solo si Google ya respondió y falló o si no había
  // intermediarios que justificaran pedirlo).
  // `polylineSource` actúa como `key` del componente Polyline para forzar su
  // unmount cuando cambiamos de fuente — sin esto, `@react-google-maps/api`
  // mantiene la línea vieja dibujada sobre el mapa al cambiar `path` y
  // termina mostrando dos polylines (la recta y la curva) superpuestas.
  let polylinePath: LatLng[] = [];
  let polylineSource: "mapbox" | "google" | "waypoints" | "none" = "none";
  if (hasRoadFollowingDense) {
    polylinePath = densePath;
    polylineSource = "mapbox";
  } else if (computedPath.length >= 2) {
    polylinePath = computedPath;
    polylineSource = "google";
  } else if (!computing && waypointPath.length >= 2) {
    // Fallback final solo cuando ya no estamos esperando a Google: evita el
    // "parpadeo" donde se dibuja la recta primero y luego encima la curva.
    polylinePath = waypointPath;
    polylineSource = "waypoints";
  }

  const usingBestRoute = polylineSource === "mapbox" || polylineSource === "google";
  const bestRouteSourceLabel = usingBestRoute ? polylineSource : null;

  // Para encuadrar la cámara usamos el mejor path disponible — si aún
  // estamos esperando a Google, encuadramos con los waypoints crudos para no
  // dejar el mapa sin centrar; cuando llega la mejor ruta, el useEffect de
  // abajo refittea bounds.
  const viewPath = polylinePath.length >= 2 ? polylinePath : waypointPath;

  const center = useMemo<LatLng | null>(() => {
    if (viewPath.length === 0) return null;
    const midIdx = Math.floor(viewPath.length / 2);
    return viewPath[midIdx];
  }, [viewPath]);

  const bounds = useMemo(() => {
    if (!isLoaded || viewPath.length < 2) return null;
    const b = new google.maps.LatLngBounds();
    viewPath.forEach((p) => b.extend(p));
    return b;
  }, [isLoaded, viewPath]);

  // Guardamos la instancia del mapa para poder reajustar bounds cuando la
  // mejor ruta (Google Directions) llegue después del primer render.
  const mapRef = useRef<google.maps.Map | null>(null);

  const onLoad = (map: google.maps.Map) => {
    mapRef.current = map;
    if (bounds) {
      map.fitBounds(bounds, 24);
    } else if (center) {
      map.setCenter(center);
      map.setZoom(13);
    }
  };

  const onUnmount = () => {
    mapRef.current = null;
  };

  useEffect(() => {
    if (!mapRef.current || !bounds) return;
    mapRef.current.fitBounds(bounds, 24);
  }, [bounds]);

  const containerHeight = 180;

  if (loading) {
    return (
      <div
        className={`rounded-lg border flex items-center justify-center ${
          isDarkMode ? "border-dark-border bg-dark-hover" : "border-gray-200 bg-gray-50"
        }`}
        style={{ height: containerHeight }}
      >
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary-500" />
      </div>
    );
  }

  // Solo consideramos "sin ruta" si no tenemos waypoints ni polyline para
  // dibujar. Mientras Google calcula la mejor ruta `polylinePath` está vacío
  // pero `waypointPath` no — en ese caso seguimos mostrando el mapa con los
  // marcadores y dejamos que el Polyline aparezca cuando llegue el resultado.
  if (waypointPath.length < 2 && polylinePath.length < 2) {
    return (
      <div
        className={`rounded-lg border flex items-center justify-center text-xs ${
          isDarkMode ? "border-dark-border bg-dark-hover text-gray-500" : "border-gray-200 bg-gray-50 text-gray-400"
        }`}
        style={{ height: containerHeight }}
      >
        {t("details.mapNoRoute")}
      </div>
    );
  }

  if (!apiKey || loadError) {
    return (
      <div
        className={`rounded-lg border flex items-center justify-center text-xs px-3 text-center ${
          isDarkMode ? "border-dark-border bg-dark-hover text-gray-500" : "border-gray-200 bg-gray-50 text-gray-400"
        }`}
        style={{ height: containerHeight }}
      >
        {loadError ? t("details.mapLoadError") : t("details.mapUnavailable")}
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div
        className={`rounded-lg border flex items-center justify-center ${
          isDarkMode ? "border-dark-border bg-dark-hover" : "border-gray-200 bg-gray-50"
        }`}
        style={{ height: containerHeight }}
      >
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-primary-500" />
      </div>
    );
  }

  // Marcadores y radios siempre se atan al primer/último waypoint del usuario
  // (no a la polyline densa) — coincide con cómo `RouteForm`/`RouteMapPreview`
  // pintan el origen y destino y sus radios.
  const markerSource = waypointPath.length >= 2 ? waypointPath : polylinePath;
  const origin = markerSource[0];
  const destination = markerSource[markerSource.length - 1];

  return (
    <div className={`rounded-lg overflow-hidden border ${
      isDarkMode ? "border-dark-border" : "border-gray-200"
    }`}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: containerHeight }}
        center={center || origin}
        zoom={13}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: "cooperative",
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
          styles: isDarkMode ? darkMapStyles : undefined,
        }}
      >
        {polylinePath.length >= 2 && (
          <Polyline
            key={`polyline-${polylineSource}`}
            path={polylinePath}
            options={{
              strokeColor: isDarkMode ? "#60a5fa" : "#2563eb",
              strokeOpacity: 0.95,
              strokeWeight: 4,
            }}
          />
        )}

        {/* Círculo de radio en el origen (mismo look que RouteMapPreview) */}
        {firstPointRadius != null && firstPointRadius > 0 && origin && (
          <Circle
            center={origin}
            radius={firstPointRadius}
            options={{
              strokeColor: "#10b981",
              strokeOpacity: 0.9,
              strokeWeight: 2,
              fillColor: "#10b981",
              fillOpacity: 0.12,
              clickable: false,
            }}
          />
        )}

        {/* Círculo de radio en el destino */}
        {lastPointRadius != null && lastPointRadius > 0 && destination && (
          <Circle
            center={destination}
            radius={lastPointRadius}
            options={{
              strokeColor: "#ef4444",
              strokeOpacity: 0.9,
              strokeWeight: 2,
              fillColor: "#ef4444",
              fillOpacity: 0.12,
              clickable: false,
            }}
          />
        )}

        <Marker
          position={origin}
          icon={{
            path: google.maps.SymbolPath.CIRCLE,
            scale: 7,
            fillColor: "#10b981",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
          }}
          title={t("details.mapOrigin")}
        />
        <Marker
          position={destination}
          icon={{
            path: google.maps.SymbolPath.CIRCLE,
            scale: 7,
            fillColor: "#ef4444",
            fillOpacity: 1,
            strokeColor: "#ffffff",
            strokeWeight: 2,
          }}
          title={t("details.mapDestination")}
        />
      </GoogleMap>

      <div
        className={`flex items-center justify-center gap-x-4 gap-y-1 py-1.5 px-2 border-t text-[10px] flex-wrap ${
          isDarkMode ? "border-dark-border bg-dark-surface text-gray-500" : "border-gray-200 bg-white text-gray-500"
        }`}
      >
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
          {t("details.mapOrigin")}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
          {t("details.mapDestination")}
        </span>
        <span className={isDarkMode ? "text-gray-600" : "text-gray-400"}>
          {t("details.mapPointCount", { count: viewPath.length })}
        </span>
        {computing && !usingBestRoute && (
          <span className={`flex items-center gap-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-[10px]" />
            {t("details.loading")}
          </span>
        )}
        {usingBestRoute && (
          <span
            className={`px-1.5 py-0.5 rounded font-medium ${
              bestRouteSourceLabel === "mapbox"
                ? isDarkMode
                  ? "bg-blue-500/20 text-blue-400"
                  : "bg-blue-50 text-blue-600"
                : isDarkMode
                  ? "bg-emerald-500/20 text-emerald-400"
                  : "bg-emerald-50 text-emerald-700"
            }`}
            title={t("details.mapBestRouteTooltip")}
          >
            {t("details.mapBestRoute")}
          </span>
        )}
      </div>
    </div>
  );
}

const darkMapStyles: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#1f2937" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1f2937" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#374151" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0f172a" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];

// ---------------------------------------------------------------------------
// Vehicle / Fleet info section
// ---------------------------------------------------------------------------

function getDeviceStatusConfig(
  status: TripVehicleInfo["device_status"],
  t: (key: string) => string,
): { color: string; label: string } {
  switch (status) {
    case "moving": return { color: "text-green-500", label: t("details.deviceStatusMoving") };
    case "online": return { color: "text-green-500", label: t("details.deviceStatusOnline") };
    case "idle": return { color: "text-amber-500", label: t("details.deviceStatusIdle") };
    case "offline": return { color: "text-red-500", label: t("details.deviceStatusOffline") };
    case "no-signal": return { color: "text-gray-500", label: t("details.deviceStatusNoSignal") };
    default: return { color: "text-gray-500", label: String(status) };
  }
}

function VehicleSection({
  info,
  isDarkMode,
  t,
}: {
  info: TripVehicleInfo;
  isDarkMode: boolean;
  t: (key: string) => string;
}) {
  const deviceCfg = getDeviceStatusConfig(info.device_status, t);

  return (
    <Section title={t("details.fleetInfo")} isDarkMode={isDarkMode}>
      {/* Vehicle */}
      <InfoRow
        icon={faTruck}
        label={t("details.vehicle")}
        value={`${info.vehicle_name}${info.vehicle_plate ? ` • ${info.vehicle_plate}` : ""}`}
        isDarkMode={isDarkMode}
      />

      {/* Device */}
      <div className="flex items-start gap-3">
        <FontAwesomeIcon
          icon={faSatelliteDish}
          className={`mt-0.5 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
        />
        <div className="min-w-0">
          <p className={`text-[11px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
            {t("details.device")}
          </p>
          <div className="flex items-center gap-2">
            <p className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
              {info.device_id}
              {info.device_model ? ` (${info.device_model})` : ""}
            </p>
            <span className={`flex items-center gap-1 text-[10px] font-medium ${deviceCfg.color}`}>
              <FontAwesomeIcon icon={faSignal} className="text-[8px]" />
              {deviceCfg.label}
            </span>
          </div>
        </div>
      </div>

      {/* Trailer */}
      {info.trailer_id && (
        <InfoRow
          icon={faTrailer}
          label={t("details.trailer")}
          value={`${info.trailer_id}${info.trailer_plate ? ` • ${info.trailer_plate}` : ""}`}
          isDarkMode={isDarkMode}
        />
      )}

      {/* Driver */}
      <div className="flex items-start gap-3">
        <FontAwesomeIcon
          icon={faUserTie}
          className={`mt-0.5 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
        />
        <div className="min-w-0">
          <p className={`text-[11px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
            {t("details.driver")}
          </p>
          <p className={`text-sm ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>
            {info.driver_name}
          </p>
          {info.driver_phone && (
            <a
              href={`tel:${info.driver_phone}`}
              className="flex items-center gap-1 text-[11px] text-primary-500 hover:underline mt-0.5"
            >
              <FontAwesomeIcon icon={faPhone} className="text-[9px]" />
              {info.driver_phone}
            </a>
          )}
        </div>
      </div>

      {/* Route / Distance */}
      <InfoRow
        icon={faRoad}
        label={t("details.route")}
        value={`${info.route_name}${info.distance_km ? ` • ${info.distance_km} km` : ""}`}
        isDarkMode={isDarkMode}
      />

      {/* ETA */}
      {info.estimated_arrival && (
        <InfoRow
          icon={faClock}
          label={t("details.eta")}
          value={info.estimated_arrival}
          isDarkMode={isDarkMode}
        />
      )}
    </Section>
  );
}

function ParticipantRow({
  participant,
  isMe,
  isOnline,
  isDarkMode,
  t,
}: {
  participant: ChatParticipant;
  isMe: boolean;
  isOnline: boolean;
  isDarkMode: boolean;
  t: (key: string) => string;
}) {
  return (
    <div
      className={`flex items-center gap-3 px-2 py-2 rounded-lg ${
        isDarkMode ? "hover:bg-dark-hover" : "hover:bg-gray-50"
      }`}
    >
      {/* Mini avatar */}
      <div className="relative flex-shrink-0">
        <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
          <FontAwesomeIcon icon={faUser} className="text-white text-xs" />
        </div>
        <FontAwesomeIcon
          icon={faCircle}
          className={`absolute -bottom-0.5 -right-0.5 text-[7px] ${
            isOnline ? "text-green-500" : "text-gray-400"
          }`}
        />
      </div>

      {/* Name + role */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span
            className={`text-sm font-medium truncate ${
              isDarkMode ? "text-gray-200" : "text-gray-800"
            }`}
          >
            {participant.display_name}
          </span>
          {isMe && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                isDarkMode
                  ? "bg-primary-500/20 text-primary-400"
                  : "bg-primary-50 text-primary-600"
              }`}
            >
              {t("details.you")}
            </span>
          )}
        </div>
        {(participant.company_name || participant.email) && (
          <p
            className={`text-[11px] truncate ${
              isDarkMode ? "text-gray-500" : "text-gray-400"
            }`}
          >
            {participant.company_name ?? participant.email}
          </p>
        )}
        {/* Chip con el company_id del usuario. Útil sobre todo en grupos /
            chats de viaje donde los miembros pueden pertenecer a tenants
            distintos. Si el backend no resolvió el company_id (chat viejo
            sin backfill todavía) simplemente no se renderiza. */}
        {participant.company_id && (
          <div className="mt-1 flex items-center gap-1">
            <FontAwesomeIcon
              icon={faUsers}
              className={`text-[8px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            />
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded font-mono truncate ${
                isDarkMode
                  ? "bg-dark-hover text-gray-400"
                  : "bg-gray-100 text-gray-600"
              }`}
              title={`${t("details.companyId")}: ${participant.company_id}`}
            >
              {participant.company_id}
            </span>
          </div>
        )}
      </div>

      {/* Admin badge placeholder */}
      {isMe && (
        <FontAwesomeIcon
          icon={faUserShield}
          className={`text-xs ${isDarkMode ? "text-gray-600" : "text-gray-300"}`}
        />
      )}
    </div>
  );
}
